/**
 * Query Tokenizer for Syntax Highlighting
 * Tokenizes Lucene-style queries into typed tokens for syntax highlighting
 */

import { fieldCategories } from '@/data/zeekFields.js'

// Build set of all valid field names from zeekFields.js
export const VALID_FIELDS = new Set()

fieldCategories.forEach(category => {
  category.fields.forEach(field => {
    VALID_FIELDS.add(field.name)
  })
})

// Token types
export const TOKEN_TYPES = {
  FIELD: 'field',           // Valid field name (orange)
  INVALID_FIELD: 'invalidField', // Unrecognized field (red)
  OPERATOR: 'operator',     // Boolean operators (purple)
  QUOTED: 'quoted',         // Quoted strings (green)
  STRUCTURAL: 'structural', // Wildcards, brackets, etc. (white)
  TEXT: 'text'              // Plain text/values (default)
}

// Word operators (case-insensitive)
const WORD_OPERATORS = ['AND', 'OR', 'NOT', 'TO']

// Symbol operators
const SYMBOL_OPERATORS = ['&&', '||', '!', '+', '-', '^', '~']

// Structural characters
const STRUCTURAL_CHARS = ['*', '?', '(', ')', '{', '}', '[', ']', ':', '\\', '/']

/**
 * Tokenize a query string into typed tokens
 * @param {string} query - The query string to tokenize
 * @returns {Array<{type: string, value: string, start: number, end: number}>}
 */
export function tokenize(query) {
  if (!query) return []

  const tokens = []
  let i = 0

  while (i < query.length) {
    const char = query[i]
    const remaining = query.slice(i)

    // Check for quoted strings
    if (char === '"') {
      const endQuote = findClosingQuote(query, i)
      const value = query.slice(i, endQuote + 1)
      tokens.push({
        type: TOKEN_TYPES.QUOTED,
        value,
        start: i,
        end: endQuote + 1
      })
      i = endQuote + 1
      continue
    }

    // Check for two-character operators first (&&, ||)
    const twoChar = query.slice(i, i + 2)
    if (twoChar === '&&' || twoChar === '||') {
      tokens.push({
        type: TOKEN_TYPES.OPERATOR,
        value: twoChar,
        start: i,
        end: i + 2
      })
      i += 2
      continue
    }

    // Check for single-character operators
    if (SYMBOL_OPERATORS.includes(char) && char !== '&' && char !== '|') {
      tokens.push({
        type: TOKEN_TYPES.OPERATOR,
        value: char,
        start: i,
        end: i + 1
      })
      i++
      continue
    }

    // Check for structural characters (except colon which is handled with fields)
    if (STRUCTURAL_CHARS.includes(char) && char !== ':') {
      tokens.push({
        type: TOKEN_TYPES.STRUCTURAL,
        value: char,
        start: i,
        end: i + 1
      })
      i++
      continue
    }

    // Check for whitespace
    if (/\s/.test(char)) {
      const match = remaining.match(/^\s+/)
      tokens.push({
        type: TOKEN_TYPES.TEXT,
        value: match[0],
        start: i,
        end: i + match[0].length
      })
      i += match[0].length
      continue
    }

    // Check for word (could be operator, field, or value)
    const wordMatch = remaining.match(/^[a-zA-Z_][a-zA-Z0-9_.-]*/)
    if (wordMatch) {
      const word = wordMatch[0]
      const afterWord = query[i + word.length]

      // Check if it's a word operator (AND, OR, NOT, TO)
      if (WORD_OPERATORS.includes(word.toUpperCase())) {
        // Make sure it's a standalone word (not part of field:value)
        const beforeChar = i > 0 ? query[i - 1] : ' '
        if (/[\s(]/.test(beforeChar) || i === 0) {
          tokens.push({
            type: TOKEN_TYPES.OPERATOR,
            value: word,
            start: i,
            end: i + word.length
          })
          i += word.length
          continue
        }
      }

      // Check if it's a field (followed by colon)
      if (afterWord === ':') {
        const isValid = VALID_FIELDS.has(word)
        tokens.push({
          type: isValid ? TOKEN_TYPES.FIELD : TOKEN_TYPES.INVALID_FIELD,
          value: word,
          start: i,
          end: i + word.length
        })
        i += word.length
        // Add the colon as structural
        tokens.push({
          type: TOKEN_TYPES.STRUCTURAL,
          value: ':',
          start: i,
          end: i + 1
        })
        i++
        continue
      }

      // Regular word/value
      tokens.push({
        type: TOKEN_TYPES.TEXT,
        value: word,
        start: i,
        end: i + word.length
      })
      i += word.length
      continue
    }

    // Check for numbers
    const numMatch = remaining.match(/^\d+\.?\d*/)
    if (numMatch) {
      tokens.push({
        type: TOKEN_TYPES.TEXT,
        value: numMatch[0],
        start: i,
        end: i + numMatch[0].length
      })
      i += numMatch[0].length
      continue
    }

    // Any other character (treat as text)
    tokens.push({
      type: TOKEN_TYPES.TEXT,
      value: char,
      start: i,
      end: i + 1
    })
    i++
  }

  return tokens
}

/**
 * Find the closing quote for a quoted string, handling escapes
 * @param {string} str - The full string
 * @param {number} start - Starting position (at the opening quote)
 * @returns {number} - Position of closing quote (or end of string)
 */
function findClosingQuote(str, start) {
  let i = start + 1
  while (i < str.length) {
    if (str[i] === '\\' && i + 1 < str.length) {
      i += 2 // Skip escaped character
      continue
    }
    if (str[i] === '"') {
      return i
    }
    i++
  }
  return str.length - 1 // Unclosed quote, return end of string
}

/**
 * Convert tokens to HTML with syntax highlighting spans
 * @param {Array} tokens - Array of tokens from tokenize()
 * @returns {string} - HTML string with highlighting spans
 */
export function tokensToHtml(tokens) {
  return tokens.map(token => {
    // Escape HTML entities
    const escaped = token.value
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')

    // Wrap in span with appropriate class
    switch (token.type) {
      case TOKEN_TYPES.FIELD:
        return `<span class="token-field">${escaped}</span>`
      case TOKEN_TYPES.INVALID_FIELD:
        return `<span class="token-invalid-field">${escaped}</span>`
      case TOKEN_TYPES.OPERATOR:
        return `<span class="token-operator">${escaped}</span>`
      case TOKEN_TYPES.QUOTED:
        return `<span class="token-quoted">${escaped}</span>`
      case TOKEN_TYPES.STRUCTURAL:
        return `<span class="token-structural">${escaped}</span>`
      case TOKEN_TYPES.TEXT:
        return `<span class="token-text">${escaped}</span>`
      default:
        return `<span class="token-text">${escaped}</span>`
    }
  }).join('')
}

/**
 * Get autocomplete suggestions for a partial field name
 * @param {string} partial - Partial field name to match
 * @returns {Array<{name: string, type: string, description: string}>}
 */
export function getFieldSuggestions(partial) {
  if (!partial) return []

  const lowerPartial = partial.toLowerCase()
  const suggestions = []

  fieldCategories.forEach(category => {
    category.fields.forEach(field => {
      if (field.name.toLowerCase().startsWith(lowerPartial)) {
        suggestions.push({
          name: field.name,
          type: field.type,
          description: field.description
        })
      }
    })
  })

  // Sort by name length (shorter = more relevant) then alphabetically
  suggestions.sort((a, b) => {
    if (a.name.length !== b.name.length) {
      return a.name.length - b.name.length
    }
    return a.name.localeCompare(b.name)
  })

  return suggestions
}

/**
 * Get all field suggestions (for showing complete list)
 * @returns {Array<{name: string, type: string, description: string, category: string}>}
 */
export function getAllFields() {
  const fields = []

  fieldCategories.forEach(category => {
    category.fields.forEach(field => {
      fields.push({
        name: field.name,
        type: field.type,
        description: field.description,
        category: category.name
      })
    })
  })

  return fields
}
